import { Tabs } from './../components/bounties/bounties-list/bounties-list.component';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  Bounty,
  GoalType,
  BeatmapGoal,
  Mod,
  PPType
} from '../interfaces/Bounty';
import { Comment } from '../components/comments/comments-section/comments-section.component';

interface BountiesListResponse {
  bounties: Bounty[];
  activeCount: number;
  likedCount: number;
  completedCount: number;
  hasMorePages: boolean;
}

interface BountyGetResponse {
  bounty: Bounty;
  comments: Comment[];
}

interface BountyClaimGetResponse {
  bounty: Bounty;
  bountyClaim: any;
}

interface CreateBountyRequest {
  goalType: GoalType;
  beatmapId?: number;
  beatmapGoals?: BeatmapGoal[];
  mods?: { [mod in Mod]: boolean };
  numberOfMisses?: number;
  accuracy?: number;
  ppType?: PPType;
  ppAmount?: number;
  tournamentName?: string;
  tournamentPlace?: number;
  reward?: number;
}

@Injectable({
  providedIn: 'root',
})
export class BountyService {
  constructor(private apiService: ApiService) { }

  public async list({
    page = 1,
    filters,
    sort,
    tab,
    search,
  }: {
    page: number;
    filters: string[];
    sort?: string;
    tab?: Tabs;
    search?: string;
  }): Promise<BountiesListResponse> {
    const filtersString = filters.length
      ? `&filters=${filters.join(',')}`
      : '';
    const sortString = sort ? `&sort=${sort}` : '';
    const tabString = tab ? `&tab=${tab}` : '';
    const searchString = search ? `&search=${search}` : '';

    return <BountiesListResponse>(
      await this.apiService
        .get(`bounties?page=${page}${filtersString}${sortString}${tabString}${searchString}`)
        .toPromise()
    );
  }

  public async getUserProfile(id: string): Promise<any> {
    return await this.apiService.get(`user/${id}`).toPromise();
  }

  public async get(id: number | string): Promise<BountyGetResponse> {
    return <BountyGetResponse>(
      await this.apiService.get(`bounties/${id}`).toPromise()
    );
  }

  public async getClaim(id: number | string): Promise<BountyClaimGetResponse> {
    return <BountyClaimGetResponse>(
      await this.apiService.get(`claims/${id}`).toPromise()
    );
  }

  public async create(
    bounty: CreateBountyRequest,
  ): Promise<{ bounty: Bounty; requiresPayment: boolean }> {
    return <{ bounty: Bounty; requiresPayment: boolean }>await this.apiService
      .post('bounties', bounty).toPromise();
  }

  public async update(bounty: Partial<CreateBountyRequest>, bountyId): Promise<{ bounty: Bounty }> {
    return <{ bounty: Bounty }>await this.apiService
      .patch(`bounties/${bountyId}`, bounty).toPromise();
  }

  public async deleteBounty(bountyId: number): Promise<void> {
    await this.apiService.delete(`bounties/${bountyId}`, {}).toPromise();
  }

  public async submitClaim(
    bountyId: number,
    evidence: string,
  ): Promise<{ claim_id: number }> {
    return <{ claim_id: number }>(
      await this.apiService
        .post(`bounties/${bountyId}/submit-claim`, { evidence })
        .toPromise()
    );
  }

  public async setClaimWinner(claimId: number): Promise<void> {
    await this.apiService
      .post(`claims/${claimId}/accept-claim`, {})
      .toPromise();
  }

  public async denyClaim(claimId: number): Promise<void> {
    await this.apiService.post(`claims/${claimId}/deny-claim`, {}).toPromise();
  }

  public async markPaymentReceived(claimId: number): Promise<void> {
    await this.apiService
      .post(`claims/${claimId}/payment-received`, {})
      .toPromise();
  }

  public getBountyTitle(bounty: Bounty) {
    switch (bounty.type) {
      case 'beatmap':
        return bounty.beatmap_name;
      case 'pp':
        if (bounty.pp_type === 'single') {
          return `Single PP score bounty`;
        } else {
          return `Total PP bounty`;
        }
      case 'tournament':
        return `${bounty.tournament_name}`;
    }
  }

  public getBountyLabelClass(bounty: Bounty) {
    switch (bounty.type) {
      case 'pp':
        return 'purple';
      case 'beatmap':
        return 'orange';
      case 'tournament':
        return 'blue';
    }
  }

  public async likeBounty(bounty: Bounty): Promise<void> {
    await this.apiService.post(`bounties/${bounty.id}/like`, {}).toPromise();
  }

  public async getStats(): Promise<any> {
    return await this.apiService.get('bounties/stats').toPromise();
  }
}
